import type { ModelNode, ModelEdge } from '../types/conceptMap';
import type { CanvasSize, PositionedNode } from './types';

const BASE_RADIUS = 180;
const RADIUS_PER_NODE = 20;

/**
 * Definition (star) layout: central concept in center, attributes in a circle around it.
 * Pure function — same input always produces same output.
 */
export function definitionLayout(
  nodes: ModelNode[],
  _edges: ModelEdge[],
  canvasSize: CanvasSize
): PositionedNode[] {
  if (nodes.length === 0) return [];

  const centerX = canvasSize.width / 2;
  const centerY = canvasSize.height / 2;

  const root = nodes.find((n) => n.role === 'root');
  const attributes = nodes.filter((n) => n.role === 'attribute');

  const positions = new Map<string, { x: number; y: number }>();

  // Root in center
  if (root) {
    positions.set(root.id, { x: centerX, y: centerY });
  }

  // Attributes in circle
  const radius = BASE_RADIUS + attributes.length * RADIUS_PER_NODE;
  attributes.forEach((node, index) => {
    const angle = (2 * Math.PI * index) / attributes.length - Math.PI / 2; // Start from top
    positions.set(node.id, {
      x: centerX + radius * Math.cos(angle),
      y: centerY + radius * Math.sin(angle),
    });
  });

  return nodes.map((node) => {
    const pos = positions.get(node.id) ?? { x: centerX, y: centerY };
    return { ...node, x: pos.x, y: pos.y };
  });
}
